import { locationManager, LocationCoords } from '../locationManager';

// Re-export for convenience
export type { LocationCoords };

// Haversine formula to calculate distance between two coordinates in kilometers
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

export interface LocationCoords {
  latitude: number;
  longitude: number;
}

export interface WithLocation {
  latitude?: number | null;
  longitude?: number | null;
}

// Filter items by distance from user location
export function filterByDistance<T extends WithLocation>(
  items: T[],
  userLocation: LocationCoords,
  maxDistanceKm: number = 50
): (T & { distance: number })[] {
  return items
    .filter((item) => item.latitude != null && item.longitude != null)
    .map((item) => ({
      ...item,
      distance: calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        item.latitude!,
        item.longitude!
      )
    }))
    .filter((item) => item.distance <= maxDistanceKm)
    .sort((a, b) => a.distance - b.distance);
}

// Get user's current location - uses centralized location manager
export function getCurrentLocation(): Promise<LocationCoords> {
  return locationManager.getLocation({
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 300000
  });
}
